import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { User, UserFilters, PaginatedResponse } from '@types'
import { userService } from '@services/userService'

interface UserState {
  users: User[]
  loading: boolean
  error: string | null
  filters: UserFilters
  pagination: {
    page: number
    pageSize: number
    total: number
  }
  selectedUsers: string[]
  statistics: {
    total: number
    active: number
    inactive: number
    banned: number
    newUsers: number
  }
}

const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
  filters: {
    status: 'all',
    city: '',
    province: '',
    startDate: '',
    endDate: '',
    page: 1,
    pageSize: 10,
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  selectedUsers: [],
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
    banned: 0,
    newUsers: 0,
  },
}

// 异步actions
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (filters: UserFilters, { rejectWithValue }) => {
    try {
      const response = await userService.getUsers(filters)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || '获取用户列表失败')
    }
  }
)

export const updateUserStatus = createAsyncThunk(
  'users/updateStatus',
  async ({ id, status }: { id: string; status: 'active' | 'inactive' | 'banned' }, { rejectWithValue }) => {
    try {
      await userService.updateUserStatus(id, status)
      return { id, status }
    } catch (error: any) {
      return rejectWithValue(error.message || '更新用户状态失败')
    }
  }
)

export const batchUpdateStatus = createAsyncThunk(
  'users/batchUpdateStatus',
  async ({ ids, status }: { ids: string[]; status: 'active' | 'inactive' | 'banned' }, { rejectWithValue }) => {
    try {
      await userService.batchUpdateStatus(ids, status)
      return { ids, status }
    } catch (error: any) {
      return rejectWithValue(error.message || '批量更新用户状态失败')
    }
  }
)

export const fetchStatistics = createAsyncThunk(
  'users/fetchStatistics',
  async (filters: UserFilters, { rejectWithValue }) => {
    try {
      const statistics = await userService.getUserStatistics(filters)
      return statistics
    } catch (error: any) {
      return rejectWithValue(error.message || '获取用户统计失败')
    }
  }
)

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<UserFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setPagination: (state, action: PayloadAction<Partial<{ page: number; pageSize: number; total: number }>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setSelectedUsers: (state, action: PayloadAction<string[]>) => {
      state.selectedUsers = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearSelected: (state) => {
      state.selectedUsers = []
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取用户列表
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<PaginatedResponse<User>>) => {
        state.loading = false
        state.users = action.payload.data
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
        }
        state.error = null
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      
      // 更新用户状态
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload
        const user = state.users.find(user => user.id === id)
        if (user) {
          user.status = status
        }
      })
      
      // 批量更新状态
      .addCase(batchUpdateStatus.fulfilled, (state, action) => {
        const { ids, status } = action.payload
        state.users.forEach(user => {
          if (ids.includes(user.id)) {
            user.status = status
          }
        })
        state.selectedUsers = []
      })
      
      // 获取统计
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload
      })
  },
})

export const { setFilters, setPagination, setSelectedUsers, clearError, clearSelected } = userSlice.actions
export default userSlice.reducer
